import React, { useState, useEffect } from "react";
import { Box, Typography, TextField, Button, MenuItem, Select, FormControl, InputLabel, Paper, Divider, Grid, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/system';
import { tokens } from "../../theme";
import Header from "../../components/Header";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.mode === 'dark' ? '#1F2A40' : '',
  boxShadow: theme.shadows[3],
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(1.5),
  fontWeight: 'bold',
  fontSize: '1rem',
}));

const AddExistingEmployeePayroll = () => {
  const [earnings, setEarnings] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [earningValues, setEarningValues] = useState({});
  const [deductionValues, setDeductionValues] = useState({});
  const [existingPayroll, setExistingPayroll] = useState(null);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultCTCEarnings, setDefaultCTCEarnings] = useState([]);
  const [defaultCTCDeductions, setDefaultCTCDeductions] = useState([]);
  const [isRegenerateMode, setIsRegenerateMode] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery("(max-width: 900px)");
  const accessToken = JSON.parse(localStorage.getItem('emsUser'))?.user?.stsTokenManager?.accessToken;

  useEffect(() => {
    fetchEmployees();
    fetchCTCDetails();
  }, []);

  useEffect(() => {
    setExistingPayroll(null);
    setLeaveSummary(null);
    setEarnings(defaultCTCEarnings);
    setDeductions(defaultCTCDeductions);
    setEarningValues({});
    setDeductionValues({});
    setIsRegenerateMode(false);
    if (selectedEmployee && selectedDate) {
      checkRegeneratePayroll();
    }
  }, [selectedEmployee, selectedDate, defaultCTCEarnings, defaultCTCDeductions]);

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/get-employees`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok) {
        setEmployees(data.employees);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      alert("Failed to load employees.");
    }
  };

  const fetchLeaveSummary = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/get-payroll-monthly-leave-summary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email_id: selectedEmployee,
          month: selectedDate,
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setLeaveSummary({
          actual_working_days: data.paid_working_days,
          num_days: data.num_days,
          LOP_DAYS: data.LOP_DAYS,
          total_leave_days: data.total_leave_days,
          leave_summary: {
            EMERGENCY: data.emergency_leave_taken || 0,
            PLANNED: data.planned_leave_taken || 0
          },
          actual_emergency_leave: data.opening_emergency_leave,
          actual_planned_leave: data.opening_planned_leave,
          subtracted_los_emergency_leave: data.closing_emergency_leave,
          subtracted_los_planned_leave: data.closing_planned_leave
        });
      }
    } catch (error) {
      console.error("Error fetching leave summary:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkRegeneratePayroll = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/re-generate-payroll`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email_id: selectedEmployee,
          selected_month: selectedDate,
        }),
      });

      const data = await response.json();
      if (response.ok && data.exists) {
        setIsRegenerateMode(true);
        const payslipData = data.payslip_data;
        const transformedEarnings = payslipData.earnings || {};
        const transformedDeductions = payslipData.deductions || {};

        setExistingPayroll({
          ...payslipData,
          earnings: transformedEarnings,
          deductions: transformedDeductions,
          total_earnings: payslipData.total_earnings || 0,
          total_deductions: payslipData.total_deductions || 0,
          total_salary: payslipData.total_salary || 0,
          one_day_salary: payslipData.one_day_salary || 0,
          LOSP: payslipData.LOSP || 0,
          LOSE: payslipData.LOSE || 0,
          total_los: payslipData.total_los || 0,
        });

        setLeaveSummary({
          actual_working_days: payslipData.actual_working_days || 0,
          num_days: payslipData.num_days || 0,
          LOP_DAYS: payslipData.LOP_DAYS || 0,
          total_leave_days: payslipData.total_leave_days || 0,
          leave_summary: payslipData.leave_summary || { EMERGENCY: 0, PLANNED: 0 },
          actual_emergency_leave: payslipData.actual_emergency_leave || 0,
          actual_planned_leave: payslipData.actual_planned_leave || 0,
          subtracted_los_emergency_leave: payslipData.subtracted_los_emergency_leave || 0,
          subtracted_los_planned_leave: payslipData.subtracted_los_planned_leave || 0
        });

        setEarnings(Object.keys(transformedEarnings));
        setDeductions(Object.keys(transformedDeductions));
        setEarningValues({ ...transformedEarnings });
        setDeductionValues({ ...transformedDeductions });
      } else {
        setIsRegenerateMode(false);
        setExistingPayroll(null);
        setLeaveSummary(null);
        setEarnings(defaultCTCEarnings);
        setDeductions(defaultCTCDeductions);
        setEarningValues({});
        setDeductionValues({});
        fetchLeaveSummary();
        checkExistingPayroll();
      }
    } catch (error) {
      console.error("Error checking for existing payslip:", error);
      setIsRegenerateMode(false);
      setExistingPayroll(null);
      setLeaveSummary(null);
      setEarnings(defaultCTCEarnings);
      setDeductions(defaultCTCDeductions);
      setEarningValues({});
      setDeductionValues({});
      fetchLeaveSummary();
      checkExistingPayroll();
    } finally {
      setIsLoading(false);
    }
  };

  const checkExistingPayroll = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/get-payroll-monthly`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          email_id: selectedEmployee,
          month: selectedDate,
        }),
      });

      const data = await response.json();
      if (response.ok && data) {
        const transformedEarnings = {};
        const transformedDeductions = {};

        if (data.monthly_earnings) {
          Object.entries(data.monthly_earnings).forEach(([key, value]) => {
            transformedEarnings[key] = value.amount_per_month;
          });
        }

        if (data.monthly_deductions) {
          Object.entries(data.monthly_deductions).forEach(([key, value]) => {
            transformedDeductions[key] = value.amount_per_month;
          });
        }

        setExistingPayroll({
          ...data,
          earnings: transformedEarnings,
          deductions: transformedDeductions,
          total_earnings: data.total_monthly_earnings || 0,
          total_deductions: data.total_monthly_deductions || 0,
          total_salary: data.total_monthly_salary || 0,
          one_day_salary: data.one_day_salary || 0,
          LOSP: data.LOSP || 0,
          LOSE: data.LOSE || 0,
          total_los: data.total_los || 0,
        });

        setEarnings(Object.keys(transformedEarnings));
        setDeductions(Object.keys(transformedDeductions));
        setEarningValues({ ...transformedEarnings });
        setDeductionValues({ ...transformedDeductions });
      } else {
        setExistingPayroll(null);
        setEarnings(defaultCTCEarnings);
        setDeductions(defaultCTCDeductions);
        setEarningValues({});
        setDeductionValues({});
      }
    } catch (error) {
      console.error("Error checking existing payroll:", error);
      setExistingPayroll(null);
      setEarnings(defaultCTCEarnings);
      setDeductions(defaultCTCDeductions);
      setEarningValues({});
      setDeductionValues({});
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCTCDetails = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}/get_ctc`, {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      if (response.ok) {
        setEarnings(data.earnings || []);
        setDeductions(data.deductions || []);
        setDefaultCTCEarnings(data.earnings || []);
        setDefaultCTCDeductions(data.deductions || []);
      } else {
        alert(data.error);
      }
    } catch (error) {
      console.error("Error fetching CTC details:", error);
      alert("Failed to load CTC details.");
    }
  };

  const handleEarningChange = (field, value) => {
    // Allow only numbers and floating-point numbers (e.g., 123, 123.45)
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setEarningValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleDeductionChange = (field, value) => {
    // Allow only numbers and floating-point numbers (e.g., 123, 123.45)
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setDeductionValues((prev) => ({ ...prev, [field]: value }));
    }
  };

  const calculateTotals = () => {
    const totalEarnings = Number.parseFloat(
      Object.values(earningValues).reduce((sum, value) => sum + (value === "" ? 0 : Number(value) || 0), 0)
    ).toFixed(2);
    const totalDeductions = Number.parseFloat(
      Object.values(deductionValues).reduce((sum, value) => sum + (value === "" ? 0 : Number(value) || 0), 0)
    ).toFixed(2);
    const totalSalary = Number.parseFloat(totalEarnings - totalDeductions).toFixed(2);
  
    return {
      totalEarnings: parseFloat(totalEarnings),
      totalDeductions: parseFloat(totalDeductions),
      totalSalary: parseFloat(totalSalary),
    };
  };

  const handlePreview = () => {
    if (!selectedEmployee || !selectedDate) {
      alert("Please select an employee and date.");
      return;
    }

    if (existingPayroll || leaveSummary) {
      setIsPreview(true);
    } else {
      alert("No payroll data available for preview.");
    }
  };

  const handleSubmit = async () => {
    if (!selectedEmployee || !selectedDate) {
      alert("Please select an employee and date.");
      return;
    }

    const { totalEarnings, totalDeductions, totalSalary } = calculateTotals();

    const payload = {
      email_id: selectedEmployee,
      selected_month: selectedDate,
      earnings: Object.fromEntries(
        Object.entries(earningValues).map(([key, value]) => [key, value === "" ? 0 : Number(value)])
      ),
      deductions: Object.fromEntries(
        Object.entries(deductionValues).map(([key, value]) => [key, value === "" ? 0 : Number(value)])
      ),
      total_earnings: totalEarnings,
      total_deductions: totalDeductions,
      total_salary: totalSalary,
      leave_summary: leaveSummary?.leave_summary || existingPayroll?.leave_summary || { EMERGENCY: 0, PLANNED: 0 },
      total_leave_days: leaveSummary?.total_leave_days || existingPayroll?.total_leave_days || 0,
      actual_emergency_leave: leaveSummary?.actual_emergency_leave || existingPayroll?.actual_emergency_leave || 0,
      actual_planned_leave: leaveSummary?.actual_planned_leave || existingPayroll?.actual_planned_leave || 0,
      subtracted_los_emergency_leave: leaveSummary?.subtracted_los_emergency_leave || existingPayroll?.subtracted_los_emergency_leave || 0,
      subtracted_los_planned_leave: leaveSummary?.subtracted_los_planned_leave || existingPayroll?.subtracted_los_planned_leave || 0,
      one_day_salary: existingPayroll?.one_day_salary || 0,
      LOSP: existingPayroll?.LOSP || 0,
      LOSE: existingPayroll?.LOSE || 0,
      total_los: existingPayroll?.total_los || 0,
      LOP_DAYS: leaveSummary?.LOP_DAYS || existingPayroll?.LOP_DAYS || 0,
      actual_working_days: leaveSummary?.actual_working_days || existingPayroll?.actual_working_days || 0,
      num_days: leaveSummary?.num_days || existingPayroll?.num_days || 0,
    };

    if (payload.LOP !== undefined) {
      delete payload.LOP;
    }

    try {
      setIsLoading(true);
      const endpoint = isRegenerateMode ? '/update-payroll' : '/store-payroll';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL_HRM}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        alert(isRegenerateMode ? "Payroll data updated successfully!" : "Payroll data saved successfully!");
        setIsPreview(false);
        checkRegeneratePayroll();
      } else {
        alert("Error: " + data.error);
      }
    } catch (error) {
      console.error("Error submitting payroll:", error);
      alert("Failed to submit payroll.");
    } finally {
      setIsLoading(false);
    }
  };

  const renderPayrollDetails = () => {
    if (!existingPayroll && !leaveSummary) return null;

    const { totalSalary } = calculateTotals();

    const displayData = {
      ...existingPayroll,
      ...(leaveSummary && {
        actual_working_days: leaveSummary.actual_working_days,
        num_days: leaveSummary.num_days,
        LOP_DAYS: leaveSummary.LOP_DAYS,
        total_leave_days: leaveSummary.total_leave_days,
        leave_summary: leaveSummary.leave_summary,
        actual_emergency_leave: leaveSummary.actual_emergency_leave,
        actual_planned_leave: leaveSummary.actual_planned_leave,
        subtracted_los_emergency_leave: leaveSummary.subtracted_los_emergency_leave,
        subtracted_los_planned_leave: leaveSummary.subtracted_los_planned_leave
      })
    };

    return (
      <StyledPaper elevation={3}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            color: colors.greenAccent[500],
            mb: 2
          }}
        >
          Payroll Details
        </Typography>
        <Divider sx={{ 
          my: 2,
          backgroundColor: colors.grey[700] 
        }} />
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Number of Days Paid:</Box> {displayData.actual_working_days || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Total days in month:</Box> {displayData.num_days || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>LOP Days:</Box> {displayData.LOP_DAYS || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Total Leaves Taken:</Box> {displayData.total_leave_days || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Emergency Leaves Taken:</Box> {displayData.leave_summary?.EMERGENCY || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Planned Leaves Taken:</Box> {displayData.leave_summary?.PLANNED || 0}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Leave Opening (Emergency):</Box> {displayData.actual_emergency_leave || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Leave Opening (Planned):</Box> {displayData.actual_planned_leave || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Leave Closing (Emergency):</Box> {displayData.subtracted_los_emergency_leave || 0}
            </Typography>
            <Typography variant="h6" color={colors.grey[100]} sx={{ mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>Leave Closing (Planned):</Box> {displayData.subtracted_los_planned_leave || 0}
            </Typography>
          </Grid>
        </Grid>
        
        <Box 
          elevation={3}
          sx={{ 
            mt: 3, 
            p: 2, 
            borderRadius: 1,
            backgroundColor: theme.palette.mode === 'dark' ? '' : '#f5f5f5',
          }}
        >
          <Typography 
            variant="h4" 
            align="center"
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
            }}
          >
           Net Salary: ₹{Number(totalSalary).toFixed(2)}
          </Typography>
        </Box>
      </StyledPaper>
    );
  };

  const renderPayrollForm = () => {
    if (isPreview && (existingPayroll || leaveSummary)) {
      const { totalEarnings, totalDeductions } = calculateTotals();

      return (
        <StyledPaper elevation={3}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              color: colors.greenAccent[500],
              mb: 3
            }}
          >
            {isRegenerateMode ? 'Re-generate Payroll Preview' : 'Payroll Preview'}
          </Typography>
          
          {renderPayrollDetails()}
  
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h5" 
                gutterBottom 
                color={colors.greenAccent[500]}
                sx={{ fontWeight: 'bold' }}
              >
                Earnings
              </Typography>
              {Object.entries(earningValues).map(([key, value]) => (
                <Box 
                  key={key} 
                  display="flex" 
                  justifyContent="space-between" 
                  mb={1}
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: colors.primary[700],
                    '&:hover': {
                      backgroundColor: colors.primary[800]
                    }
                  }}
                >
                  <Typography color={colors.grey[100]}>{key}:</Typography>
                  <Typography color={colors.grey[100]}>₹{value === "" ? 0 : value}</Typography>
                </Box>
              ))}
              <Divider sx={{ 
                my: 2,
                backgroundColor: colors.grey[700] 
              }} />
              <Box 
                display="flex" 
                justifyContent="space-between" 
                sx={{ 
                  fontWeight: 'bold',
                  p: 2,
                  backgroundColor: colors.primary[700],
                  borderRadius: 1
                }}
              >
                <Typography color={colors.grey[100]}>Total Earnings:</Typography>
                <Typography color={colors.grey[100]}>₹{totalEarnings}</Typography>
              </Box>
            </Grid>
  
            <Grid item xs={12} md={6}>
              <Typography 
                variant="h5" 
                gutterBottom 
                color={colors.greenAccent[500]}
                sx={{ fontWeight: 'bold' }}
              >
                Deductions
              </Typography>
              {Object.entries(deductionValues)
                .filter(([key]) => key !== "LOP")
                .map(([key, value]) => (
                  <Box 
                    key={key} 
                    display="flex" 
                    justifyContent="space-between" 
                    mb={1}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      backgroundColor: colors.primary[700],
                      '&:hover': {
                        backgroundColor: colors.primary[800]
                      }
                    }}
                  >
                    <Typography color={colors.grey[100]}>{key}:</Typography>
                    <Typography color={colors.grey[100]}>₹{value === "" ? 0 : value}</Typography>
                  </Box>
                ))}
              <Divider sx={{ 
                my: 2,
                backgroundColor: colors.grey[700] 
              }} />
              <Box 
                display="flex" 
                justifyContent="space-between" 
                sx={{ 
                  fontWeight: 'bold',
                  p: 2,
                  backgroundColor: colors.primary[700],
                  borderRadius: 1
                }}
              >
                <Typography color={colors.grey[100]}>Total Deductions:</Typography>
                <Typography color={colors.grey[100]}>₹{totalDeductions}</Typography>
              </Box>
            </Grid>
          </Grid>
  
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button
              onClick={() => setIsPreview(false)}
              variant="contained"
              sx={{ 
                width: '48%',
                backgroundColor: colors.redAccent[700],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.redAccent[800]
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{ 
                width: '48%',
                backgroundColor: colors.greenAccent[700],
                color: colors.grey[100],
                '&:hover': {
                  backgroundColor: colors.greenAccent[800]
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : (isRegenerateMode ? 'Re-generate Payslip' : 'Generate Payslip')}
            </Button>
          </Box>
        </StyledPaper>
      );
    }
  
    return (
      <>
        {(existingPayroll || leaveSummary) && renderPayrollDetails()}
  
        <StyledPaper elevation={3}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              color: colors.greenAccent[500],
              mb: 2
            }}
          >
            Earnings
          </Typography>
          {earnings.map((field, index) => (
            <TextField
              key={index}
              label={field}
              type="text" // Changed from "number" to "text"
              variant="outlined"
              fullWidth
              margin="normal"
              value={earningValues[field] || ""}
              onChange={(e) => handleEarningChange(field, e.target.value)}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ 
                    mr: 1, 
                    color: colors.grey[100],
                    fontWeight: 'bold'
                  }}>
                    ₹
                  </Typography>
                ),
                style: { 
                  color: colors.grey[100]
                }
              }}
              InputLabelProps={{
                style: { color: colors.grey[100] }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { 
                    borderColor: colors.grey[400] 
                  },
                  "&:hover fieldset": { 
                    borderColor: colors.greenAccent[500] 
                  },
                  "&.Mui-focused fieldset": { 
                    borderColor: colors.greenAccent[500] 
                  },
                },
              }}
            />
          ))}
        </StyledPaper>
  
        <StyledPaper elevation={3}>
          <Typography 
            variant="h5" 
            gutterBottom 
            sx={{ 
              fontWeight: 'bold', 
              color: colors.greenAccent[500],
              mb: 2
            }}
          >
            Deductions
          </Typography>
          {deductions
            .filter(field => isPreview || field !== "LOP")
            .map((field, index) => (
              <TextField
                key={index}
                label={field}
                type="text" // Changed from "number" to "text"
                variant="outlined"
                fullWidth
                margin="normal"
                value={deductionValues[field] || ""}
                onChange={(e) => handleDeductionChange(field, e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Typography sx={{ 
                      mr: 1, 
                      color: colors.grey[100],
                      fontWeight: 'bold'
                    }}>
                      ₹
                    </Typography>
                  ),
                  style: { 
                    color: colors.grey[100]
                  }
                }}
                InputLabelProps={{
                  style: { color: colors.grey[100] }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { 
                      borderColor: colors.grey[400] 
                    },
                    "&:hover fieldset": { 
                      borderColor: colors.greenAccent[500] 
                    },
                    "&.Mui-focused fieldset": { 
                      borderColor: colors.greenAccent[500] 
                    },
                  },
                }}
              />
            ))}
        </StyledPaper>
  
        <Button
          variant="contained"
          fullWidth
          onClick={handlePreview}
          disabled={isLoading || !selectedEmployee || !selectedDate}
          sx={{
            mt: 3,
            p: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem',
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.blueAccent[800]
            },
            '&:disabled': {
              backgroundColor: colors.grey[700],
              color: colors.grey[500]
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            isRegenerateMode ? 'Preview Re-generate Payslip' : 'Preview Payslip'
          )}
        </Button>
      </>
    );
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="PAYROLL MANAGEMENT" subtitle="Manage employee payroll data" />
      </Box>

      <Box 
        sx={{ 
          maxWidth: 900, 
          mx: 'auto', 
          p: isSmallScreen ? 2 : 3 
        }}
      >
        <StyledPaper elevation={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel 
                  id="employee-select-label"
                  sx={{
                    color: colors.grey[200],
                    "&.Mui-focused": {
                      color: colors.grey[200],
                    },
                    "&:hover": {
                      color: colors.grey[200],
                    },
                  }}
                >
                  Select Employee
                </InputLabel>
                <Select
                  labelId="employee-select-label"
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  label="Select Employee"
                  disabled={isLoading}
                  renderValue={(selectedEmail) => {
                    if (!selectedEmail) {
                      return <em style={{ color: colors.grey[500] }}>Choose an Employee</em>;
                    }
                    const selectedEmp = employees.find(emp => emp.email === selectedEmail);
                    return selectedEmp ? selectedEmp.name : selectedEmail;
                  }}
                  sx={{
                    color: colors.grey[100],
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.grey[400],
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.greenAccent[500],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.greenAccent[500],
                    },
                    "& .MuiSvgIcon-root": {
                      color: colors.grey[100],
                    },
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: colors.grey[500] }}>Choose an Employee</em>
                  </MenuItem>
                  {employees.map((employee) => (
                    <MenuItem
                      key={employee.id}
                      value={employee.email}
                      sx={{
                        color: colors.grey[100],
                        backgroundColor: colors.primary[400],
                        "&:hover": {
                          backgroundColor: colors.grey[500],
                        },
                      }}
                    >
                      {employee.name} - {employee.email}
                    </MenuItem>
                  ))}
                </Select>

              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
  <TextField
    label="Select Month"
    type="month"
    fullWidth
    InputLabelProps={{ 
      shrink: true, 
      style: { color: colors.grey[100] } 
    }}
    value={selectedDate}
    onChange={(e) => {
      const selected = e.target.value;
      // const currentDate = new Date();
      // const currentYearMonth = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
      
      // if (selected <= currentYearMonth) {
      setSelectedDate(selected);
      // }
    }}
    disabled={isLoading}
    inputProps={{
      // max: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
    }}
    sx={{
      color: colors.grey[100],
      "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: colors.grey[400] },
        "&:hover fieldset": { borderColor: colors.greenAccent[500] },
        "&.Mui-focused fieldset": { borderColor: colors.greenAccent[500] },
      },
      "& input": {
        color: colors.grey[100],
        "&::-webkit-calendar-picker-indicator": {
          filter: theme.palette.mode === "dark" ? "invert(1)" : "invert(0)",
        },
      },
    }}
  /> 
</Grid>
          </Grid>
        </StyledPaper>

        {renderPayrollForm()}
      </Box>
    </Box>
  );
};

export default AddExistingEmployeePayroll;